import { colors } from '@atlaskit/theme';
import React from 'react';
import styled from 'styled-components';

import { Container, Contributor, ObjectItem } from '../../clients/activities/model';
import { redirect } from '../../facade/window';
import { useAnalytics } from '../../hooks/analytics';
import { EVENT_TYPE } from '../../model/events';
import { isNewTabClick } from '../../util/event';
import { hideOnMobile, onDesktop, onMobile } from '../mobile/responsive';
import { ellipsis } from '../styled/text';

import Contributors from './Contributors';

const Row = styled.a`
  display: flex;
  justify-content: space-between;
  border-radius: 3px;
  color: ${colors.N800};
  padding: 8px;

  &:focus,
  &:hover {
    background: ${colors.N10};
    color: ${colors.N800};
    text-decoration: none;
  }

  // Reduce horizontal padding so we can maximise the space in mobile
  ${onMobile(`
    padding-left: 0;
    padding-right: 0;
  `)}
`;

const ObjectDetail = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  // Trick to get ellipsis to work in a flex box
  min-width: 0;

  // We can afford a bit more spacing for desktop, but smaller for mobile
  ${onDesktop(`
    margin: 0 16px;
  `)}

  ${onMobile(`
    margin: 0 8px;
  `)}
`;

const ObjectName = styled.div`
  ${ellipsis};
  font-size: 14px;

  ${Row}:hover &,
  ${Row}:focus & {
    color: ${colors.B400};
  }
`;
const ObjectContainer = styled.div`
  font-size: 11px;
  color: ${colors.N200};

  &:link {
    text-decoration: none;
  }
`;

const ObjectIcon = styled.img`
  flex-grow: 0;
  flex-shrink: 0;
  width: 24px;
  height: 24px;

  align-self: center;

  // Bigger icon for desktop
  ${onDesktop(`
    width: 24px;
    height: 24px;
  `)}

  // Smaller icon for mobile
  ${onMobile(`
    width: 16px;
    height: 16px;
  `)}
`;

const Extra = styled.div`
  font-size: 11px;
  color: ${colors.N200};
  flex-basis: 180px;
  max-width: 20%;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  &:link {
    text-underline: none;
  }

  // Hide this column on mobile view
  ${hideOnMobile()}
`;

const ContributorsContainer = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  // Fixed the contributors width so we can horizontally align the "extra" column
  // This is not an issue for mobile because we hide the "extra" column. See above.
  ${onDesktop(`
    width: 128px;
  `)}
`;

interface Props {
  analyticsAttributes: {
    eventType?: EVENT_TYPE;
    tab: 'viewed' | 'workedOn' | 'search';
  };
  children: React.ReactChild;
  container: Container;
  contributors: Contributor[];
  fullIndex: number;
  object: ObjectItem;
}

function getSiteBaseUrl(urlString: string) {
  const url = new URL(urlString);
  return url.origin;
}

export default function YourWorkRow(props: Props) {
  const { analyticsAttributes, children, container, contributors, fullIndex, object } = props;
  const analytics = useAnalytics();
  return (
    <Row
      href={object.url}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        const isOpeningInNewTab = isNewTabClick(e);
        if (!isOpeningInNewTab) {
          e.preventDefault();
        }
        analytics
          .ui('yourWorkRow', 'clicked', {
            ...analyticsAttributes,
            fullIndex,
          })
          .then(() => {
            if (!isOpeningInNewTab) {
              redirect(object.url);
            }
          });
      }}
    >
      <ObjectIcon alt="" src={object.iconURL} />
      <ObjectDetail>
        <ObjectName data-test-id="objectName">{object.name}</ObjectName>
        <ObjectContainer>{container.name}</ObjectContainer>
      </ObjectDetail>
      <Extra>{children}</Extra>
      <ContributorsContainer>
        <Contributors siteUrl={getSiteBaseUrl(object.url)} contributors={contributors} cloudId={object.cloudID} />
      </ContributorsContainer>
    </Row>
  );
}
